import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment/moment';
import ApiGrid from '@components-clean/core/api-grid';
import { DEFAULT_GRID_TABLE } from '@components-clean/core/api-grid/api-grid.helper';
import CoreSplitter from '@components-clean/core/core-splitter';
import { CreateDataListingChangeStateBadgeCell } from '@components-clean/core/grid/custom-cell/badge-cell';
import FeGrid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { EditReviewDto } from '@interfaces-clean/reviews.interface';
import { ISMDataset } from '@pages-clean/central-monitoring/interfaces/context';
import { DataReviewType } from 'types/graphql-global-types';
import { useBoolean, useTranslation } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable } from '@queries/generated/central-monitoring.generated';

type DataTableChangeRow = {
    dataId: number;
    onWatch: boolean;
    keyName: string;
    status: string;
    currentDatasetValue: string | null;
    datasetInFocusValue: string | null;
    lastReviewedDatasetValue: string | null;
};

const StateCell = CreateDataListingChangeStateBadgeCell();

// Validates shape of a DataTableChangeRow, which is returned as a JSON string.
// Returns null if JSON does not have expected shape.
function parseDataTableChangeRow(row: unknown): DataTableChangeRow | null {
    if (!(row instanceof Object)) {
        return null;
    }

    if (!('data_id' in row) || typeof row.data_id !== 'number') {
        return null;
    }
    if (!('on_watch' in row) || typeof row.on_watch !== 'boolean') {
        return null;
    }
    if (!('key_name' in row) || typeof row.key_name !== 'string') {
        return null;
    }
    if (!('current_review' in row) || (row.current_review != null && typeof row.current_review !== 'string')) {
        return null;
    }
    if (!('dataset_in_focus' in row) || (row.dataset_in_focus != null && typeof row.dataset_in_focus !== 'string')) {
        return null;
    }
    if (
        !('last_reviewed_dataset' in row) ||
        (row.last_reviewed_dataset != null && typeof row.last_reviewed_dataset !== 'string')
    ) {
        return null;
    }

    let status;
    if (row.last_reviewed_dataset == null || row.last_reviewed_dataset === 'N/A') {
        status = 'New';
    } else {
        status = row.last_reviewed_dataset === row.current_review ? 'Unchanged' : 'Changed';
    }

    return {
        dataId: row.data_id,
        onWatch: row.on_watch,
        keyName: row.key_name,
        status,
        currentDatasetValue: row.current_review ?? null,
        datasetInFocusValue: row.dataset_in_focus ?? null,
        lastReviewedDatasetValue: row.last_reviewed_dataset ?? null,
    };
}

// Validates shape of a DataTableChangeRows, which is returned as a JSON string.
// Returns empty array if response is not an array.
// Removes any rows which do not have the expected shape.
function parseDataTableChangeRows(jsonString: string): DataTableChangeRow[] {
    let json;
    try {
        json = JSON.parse(jsonString);
    } catch {
        return [];
    }

    if (!Array.isArray(json)) {
        return [];
    }

    return json.map(parseDataTableChangeRow).filter((x) => x != null);
}

type Props = {
    datatables: dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable[];
    datatablesGroupByField?: string;
    selectedDatatable: dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable | undefined;
    setSelectedDatatable: React.Dispatch<
        React.SetStateAction<dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable | undefined>
    >;

    studyRef: string;
    studyNumber: string;
    currentDataset: ISMDataset;
    reviewDataset?: ISMDataset;
    lastReviewedDataset?: ISMDataset;
    subjectId: number;
    subjectNumber: string;
    activeReview?: EditReviewDto;

    noPadding?: boolean;
    gridFilterable?: boolean;
};

const SubjectMonitoringDataTables = ({
    datatables,
    datatablesGroupByField,
    selectedDatatable,
    setSelectedDatatable,

    studyRef,
    studyNumber,
    currentDataset,
    reviewDataset,
    lastReviewedDataset,
    subjectId,
    subjectNumber,
    activeReview,

    noPadding,
    gridFilterable,
}: Props) => {
    const showOnWatchOnly = useBoolean(true);
    const [changesRows, setChangesRows] = useState<DataTableChangeRow[]>();

    const { t: tCM } = useTranslation('cm');
    const { t } = useTranslation('sm', { keyPrefix: 'grid.dataListingReview' });

    const lastReviewedDatasetTime = lastReviewedDataset?.dataCutDate.getTime();
    const currentDatasetTime = currentDataset.dataCutDate.getTime();
    const reviewDatasetTime = reviewDataset?.dataCutDate.getTime();

    const showCurrentDataset =
        currentDatasetTime !== lastReviewedDatasetTime && currentDatasetTime !== reviewDatasetTime;

    const currentDatasetBeforeLastReviewedDataset =
        lastReviewedDatasetTime != null && currentDatasetTime < lastReviewedDatasetTime;
    const currentDatasetAfterReviewDataset = reviewDatasetTime != null && currentDatasetTime > reviewDatasetTime;

    const filenameForExport = `${studyNumber}_${subjectNumber ?? ''}_${selectedDatatable?.name}_${moment(
        currentDataset.dataCutDate,
    ).format('DD-MMM-yyyy')}`;

    return (
        <CoreSplitter
            className="!k-h-full"
            data-testid="sm-data-listings-splitter"
            orientation={SplitterOrientation.vertical}
        >
            <div className="k-h-full">
                <ApiGrid
                    title={datatables.map((x) => ({
                        ...x,
                        id: x?.id as string,
                        label: x?.name,
                        active: x?.active,
                    }))}
                    selectedTitle={
                        selectedDatatable && {
                            ...selectedDatatable,
                            id: selectedDatatable.id as string,
                            label: selectedDatatable.name,
                        }
                    }
                    onTitleChange={(e) => {
                        if (e.value) {
                            setSelectedDatatable(
                                e.value as dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable,
                            );
                        }
                    }}
                    titleGroupByField={datatablesGroupByField}
                    titleReadOnly={datatables.length === 1}
                    testDataId="datatables-grid"
                    maxHeight="auto"
                    table={DEFAULT_GRID_TABLE}
                    dataItemKey="Data_id"
                    gridParams={{
                        grid: 'SMDataTable',
                        studyRef,
                        dataset: currentDataset.id,
                        datatable: selectedDatatable ? (selectedDatatable?.id as string) : '',
                        subjectId: subjectId as unknown as string,
                        isSubjectLevelTable: selectedDatatable?.dataSource === 'subjectsAll',
                        reviewRecordId: activeReview?.recordId,
                    }}
                    filterable={gridFilterable ?? selectedDatatable?.type !== DataReviewType.DATA}
                    noTitleMessage={
                        !selectedDatatable?.active ? tCM('dataView.datatables.inActiveDataTable') : undefined
                    }
                    fetchData={selectedDatatable?.active || false}
                    screenFilterable={selectedDatatable?.type === DataReviewType.DATA}
                    exportFilename={filenameForExport}
                    gridDownloadDisabled={activeReview?.recordId != null}
                    showAddIcon={false}
                    showPlaceholder={false}
                    noPadding={noPadding}
                    rowHeight={24}
                    scrollable="virtual"
                    selectable={{
                        mode: 'single',
                        enabled: !!activeReview,
                    }}
                    onSelectionChange={(e) => {
                        if (!(e.startDataItem instanceof Object) || !('changes' in e.startDataItem)) {
                            return;
                        }
                        if (typeof e.startDataItem.changes !== 'string') {
                            return;
                        }

                        setChangesRows(parseDataTableChangeRows(e.startDataItem.changes));
                    }}
                />
            </div>

            {changesRows != null && (
                <div className="k-h-full">
                    {currentDatasetBeforeLastReviewedDataset || currentDatasetAfterReviewDataset ? (
                        <InfoField type="Info" message={t('datasetOutsideOfReviewPeriod')} layout="center" />
                    ) : (
                        <FeGrid
                            testDataId="changes-grid"
                            title={t('title')}
                            maxHeight="auto"
                            gridDownloadDisabled
                            gridSettingsMenuDisabled
                            showAddIcon={false}
                            filterable={false}
                            reorderable={false}
                            customGridMenuItems={[
                                {
                                    'data-testid': 'riskMonitoring-grid-show-deleted-records',
                                    title: t('actions.toggleOnWatchFields'),
                                    customIcon: (
                                        <FontAwesomeIcon icon={[showOnWatchOnly.value ? 'fas' : 'far', 'eye']} />
                                    ),
                                    onClick: showOnWatchOnly.toggle,
                                },
                            ]}
                            columns={[
                                {
                                    id: 'state',
                                    field: 'status',
                                    title: t('columns.status'),
                                    minWidth: 160,
                                    locked: true,
                                    lockedByDefault: true,
                                    reorderable: false,
                                    cells: { data: StateCell },
                                },
                                {
                                    id: 'field',
                                    field: 'keyName',
                                    title: t('columns.field'),
                                    minWidth: 160,
                                    locked: true,
                                    lockedByDefault: true,
                                    reorderable: false,
                                },
                                {
                                    id: 'state',
                                    field: 'state',
                                    title: t('columns.valueHistory.title'),
                                    sortable: false,
                                    reorderable: false,
                                    children: (
                                        [
                                            lastReviewedDataset != null
                                                ? {
                                                      id: 'state:last-reviewed',
                                                      field: 'lastReviewedDatasetValue',
                                                      title: `${t('columns.valueHistory.columns.lastReviewedDataset')}: ${
                                                          stringDateWithoutTimeFromUtc(
                                                              lastReviewedDataset.dataCutDate,
                                                              'DD-MMM-yyyy',
                                                          ) ?? ''
                                                      }`,
                                                      minWidth: 160,
                                                      sortable: false,
                                                  }
                                                : null,
                                            showCurrentDataset
                                                ? {
                                                      id: 'state:dataset-in-focus',
                                                      field: 'datasetInFocusValue',
                                                      title: `${t('columns.valueHistory.columns.datasetInFocus')}: ${
                                                          stringDateWithoutTimeFromUtc(
                                                              currentDataset.dataCutDate,
                                                              'DD-MMM-yyyy',
                                                          ) ?? ''
                                                      }`,
                                                      minWidth: 160,
                                                      sortable: false,
                                                  }
                                                : null,
                                            reviewDataset != null
                                                ? {
                                                      id: 'state:dataset-in-review',
                                                      field: 'currentDatasetValue',
                                                      title: `${t('columns.valueHistory.columns.datasetInReview')}: ${
                                                          stringDateWithoutTimeFromUtc(
                                                              reviewDataset.dataCutDate,
                                                              'DD-MMM-yyyy',
                                                          ) ?? ''
                                                      }`,
                                                      minWidth: 160,
                                                      sortable: false,
                                                  }
                                                : null,
                                        ] satisfies (GridColumnProps | null)[]
                                    ).filter((x) => x != null),
                                },
                            ]}
                            data={showOnWatchOnly.value ? changesRows.filter((x) => x.onWatch) : changesRows}
                        />
                    )}
                </div>
            )}
        </CoreSplitter>
    );
};

export default SubjectMonitoringDataTables;
